/**
 * Playbook Detail — Full detail page for a single playbook template
 *
 * Features:
 * - Back navigation to /automate/playbooks
 * - Header with playbook name, agent badge, category badge, estimated duration
 * - Full description text
 * - Stats row: steps, success rate, duration, tags
 * - Vertical step timeline with tool badges, failure policy, branch conditions
 * - Action buttons: use as automation, open in workflow builder
 * - Not-found state for invalid IDs
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft, Clock, CheckCircle, Layers, Tag, Loader2,
  Sparkles, Play, Wrench, AlertOctagon, ArrowRightCircle, GitBranch,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AgentIcon } from '@/components/shared/icon-picker';
import { cn } from '@/lib/utils';
import { PLAYBOOK_CATEGORY_STYLES } from '@/lib/mock-automate-data';
import { usePlaybook, useInstantiatePlaybook } from '@/lib/use-automate';

export const Route = createFileRoute('/automate/playbooks/$playbookId')({
  component: PlaybookDetailPage,
});

const FAILURE_POLICY_STYLES: Record<string, { label: string; color: string }> = {
  stop: { label: 'Stop on failure', color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
  continue: { label: 'Continue on failure', color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30' },
  branch: { label: 'Branch on failure', color: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30' },
};

function PlaybookDetailPage() {
  const navigate = useNavigate();
  const { playbookId } = Route.useParams();
  // Real-data: playbooks table
  const { data: playbook } = usePlaybook(playbookId);
  const instantiate = useInstantiatePlaybook();

  // #625 — both action paths instantiate a real workflow row from the
  // playbook template. We surface an explicit confirm dialog before
  // mutating so neither button silently creates a tenant workflow the
  // user did not intend.
  const handleInstantiate = (intent: 'use' | 'open') => {
    if (!playbook || instantiate.isPending) return;
    // #625 follow-up — the native window.confirm() gate blocked this
    // action entirely (auto-dismissed in QA/automation, returning false),
    // making both buttons silent no-ops. Instantiation only creates a
    // reversible DRAFT workflow, so we fire the (tenant-scoped,
    // write:workflows-gated) mutation directly; the toast + navigation
    // below give explicit feedback. `intent` differentiates the toast.
    instantiate.mutate(
      { playbookId: playbook.id },
      {
        onSuccess: ({ id }) => {
          toast.success('Workflow created from playbook', {
            description:
              intent === 'open'
                ? `Opening "${playbook.name}" in the builder…`
                : `Created "${playbook.name}" — opening in the builder…`,
          });
          navigate({ to: '/automate/workflows/$id', params: { id } } as never);
        },
        onError: (err) => {
          toast.error('Could not create workflow from playbook', {
            description: err.message ?? 'Server returned an error.',
          });
        },
      },
    );
  };

  if (!playbook) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <AlertOctagon className="h-10 w-10 text-muted-foreground/20 mb-3" />
        <h2 className="text-lg font-semibold">Playbook Not Found</h2>
        <p className="text-sm text-muted-foreground mt-1">
          The playbook "{playbookId}" does not exist.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="mt-4 text-xs gap-1"
          onClick={() => navigate({ to: '/automate/playbooks' })}
        >
          <ArrowLeft className="h-3 w-3" />
          Back to Playbooks
        </Button>
      </div>
    );
  }

  const catStyle = PLAYBOOK_CATEGORY_STYLES[playbook.category];

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Header */}
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="text-xs gap-1 -ml-2 text-muted-foreground hover:text-foreground"
          title="Back to playbook templates"
          onClick={() => navigate({ to: '/automate/playbooks' })}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Playbooks
        </Button>

        <div className="flex items-start gap-4">
          <AgentIcon iconName={playbook.agentIcon} colorName={playbook.agentIconColor} size="lg" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-semibold">{playbook.name}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant="outline" className={cn('text-[9px]', catStyle.color)}>
                {catStyle.label}
              </Badge>
              <Badge variant="outline" className="text-[9px] bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20">
                <Sparkles className="h-2.5 w-2.5 mr-0.5" />
                {playbook.agentName}
              </Badge>
              <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                {playbook.estimatedDuration}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="rounded-lg border bg-muted/30 p-4">
        <p className="text-sm text-muted-foreground leading-relaxed">{playbook.description}</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-card p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <Layers className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Steps</span>
          </div>
          <p className="text-lg font-semibold">{playbook.steps.length}</p>
        </div>
        <div className="rounded-lg border bg-card p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <CheckCircle className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Success Rate</span>
          </div>
          <p className="text-lg font-semibold">
            {/* Backend stores 0..100; clamp + display (#100). */}
            {playbook.successRate > 0
              ? `${Math.round(Math.min(100, Math.max(0, playbook.successRate)))}%`
              : 'N/A'}
          </p>
        </div>
        <div className="rounded-lg border bg-card p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <Clock className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Duration</span>
          </div>
          <p className="text-sm font-semibold">{playbook.estimatedDuration}</p>
        </div>
        <div className="rounded-lg border bg-card p-3 text-center">
          <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
            <Tag className="h-3.5 w-3.5" />
            <span className="text-[10px] font-medium uppercase tracking-wider">Tags</span>
          </div>
          <p className="text-lg font-semibold">{playbook.tags.length}</p>
        </div>
      </div>

      {/* Tags */}
      {playbook.tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {playbook.tags.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-0.5 px-2 py-1 rounded-md text-[10px] bg-muted text-muted-foreground font-medium"
            >
              <Tag className="h-2.5 w-2.5" />
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Step Timeline */}
      <div>
        <h2 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <Layers className="h-4 w-4 text-primary" />
          Procedure Steps
        </h2>
        <div className="relative ml-4">
          {playbook.steps.map((step, idx) => {
            const isLast = idx === playbook.steps.length - 1;
            const failureStyle = FAILURE_POLICY_STYLES[step.onFailure];
            return (
              <div key={step.step} className="relative flex gap-4 pb-6">
                {/* Vertical line */}
                {!isLast && (
                  <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border" />
                )}
                {/* Step number circle */}
                <div className="relative z-10 flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-semibold">
                  {step.step}
                </div>
                {/* Step content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <h3 className="text-sm font-medium">{step.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {step.toolUsed && (
                      <Badge
                        variant="outline"
                        className="text-[9px] font-mono bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30"
                      >
                        <Wrench className="h-2.5 w-2.5 mr-0.5" />
                        {step.toolUsed}
                      </Badge>
                    )}
                    {failureStyle && (
                      <Badge variant="outline" className={cn('text-[9px]', failureStyle.color)}>
                        {step.onFailure === 'stop' && <AlertOctagon className="h-2.5 w-2.5 mr-0.5" />}
                        {step.onFailure === 'continue' && <ArrowRightCircle className="h-2.5 w-2.5 mr-0.5" />}
                        {step.onFailure === 'branch' && <GitBranch className="h-2.5 w-2.5 mr-0.5" />}
                        {failureStyle.label}
                      </Badge>
                    )}
                  </div>
                  {step.branchCondition && (
                    <p className="text-[10px] text-muted-foreground mt-1.5 italic flex items-center gap-1">
                      <GitBranch className="h-3 w-3" />
                      Branch: {step.branchCondition}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action buttons — both create a real workflow from the playbook
          template via POST /playbooks/:id/instantiate (#105). #625 —
          we now confirm before mutating and the secondary button is
          labelled to make the side-effect explicit. */}
      <div className="border-t pt-4 flex items-center gap-3">
        <Button
          className="gap-1.5"
          onClick={() => handleInstantiate('use')}
          disabled={instantiate.isPending}
          title="Create a draft workflow from this playbook"
        >
          {instantiate.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Play className="h-3.5 w-3.5" />
          )}
          {instantiate.isPending ? 'Creating workflow…' : 'Use as Workflow'}
        </Button>
        <Button
          variant="outline"
          className="gap-1.5"
          onClick={() => handleInstantiate('open')}
          disabled={instantiate.isPending}
          title="Create a draft workflow from this playbook and open it in the builder"
        >
          <GitBranch className="h-3.5 w-3.5" />
          Create &amp; Open in Builder
        </Button>
      </div>
    </div>
  );
}
